import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Eye } from 'lucide-react';

import Button from 'src/componentes/button';
import {
  FormularioGlobalBuilder,
  FormularioGlobalCopiarColar,
} from 'src/componentes/formulario-global';
import {
  countPerguntas,
  getFormularioValidationErrors,
  getFormularioValidationToastError,
  hasValidationErrors,
  normalizeFormulario as normalizeFormularioGlobal,
  serializeFormulario as serializeFormularioGlobal,
} from 'src/componentes/formulario-global/formularioGlobalModel';
import {
  copyFormularioStructure,
  hasFormularioGlobalBranching,
} from 'src/componentes/formulario-global/formularioGlobalBranching';
import http from 'src/services/httpService';
import useAuth from 'src/hooks/useAuth';
import errorHandler from 'src/utils/error-handler';

const TIPO_FORMULARIO_PESQUISA_CLIMA = {
  id: '1',
  codigo: 'PesquisaClima',
  label: 'labelPesquisaClima',
};

const createEmptyFormulario = () => ({
  id: 0,
  tipoFormulario: TIPO_FORMULARIO_PESQUISA_CLIMA,
  titulo: '',
  descricao: '',
  ativo: true,
  bloqueado: false,
  secoes: [],
  perguntas: [],
  usos: [],
});

const normalizeFormulario = (formulario, intl) =>
  normalizeFormularioGlobal(formulario, intl, TIPO_FORMULARIO_PESQUISA_CLIMA);

const serializeFormulario = (formulario, entidadeId, intl) =>
  serializeFormularioGlobal(
    formulario,
    entidadeId,
    intl,
    TIPO_FORMULARIO_PESQUISA_CLIMA
  );

const FormularioPesquisaClimaBuilder = forwardRef(
  (
    {
      active,
      disabled,
      entidadeId,
      formularioId,
      onPreview,
      onBranchingChange,
      randomizarPerguntas = false,
    },
    ref
  ) => {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const { terms: resources } = useAuth();
    const [formulario, setFormulario] = useState(createEmptyFormulario());
    const [isLoading, setIsLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const hasLoadedRef = useRef(false);
    const originalRef = useRef(JSON.stringify(createEmptyFormulario()));
    const formularioRef = useRef(formulario);
    const formularioSerializedRef = useRef(JSON.stringify(formulario));
    const hasChangesRef = useRef(hasChanges);
    const validationErrorsRef = useRef({});
    const isEntidadePersisted = entidadeId > 0;
    const hasExistingFormulario = formularioId > 0;

    const updateFormulario = (
      nextFormulario,
      updateOriginal = false,
      notifyBranching = true
    ) => {
      const normalizedFormulario = normalizeFormulario(nextFormulario, intl);
      const serialized = JSON.stringify(normalizedFormulario);

      if (!updateOriginal && serialized === formularioSerializedRef.current) {
        return;
      }

      formularioRef.current = normalizedFormulario;
      formularioSerializedRef.current = serialized;
      setFormulario(normalizedFormulario);
      if (notifyBranching)
        onBranchingChange?.(hasFormularioGlobalBranching(normalizedFormulario));

      if (hasValidationErrors(validationErrorsRef.current)) {
        const nextValidationErrors = getFormularioValidationErrors(
          normalizedFormulario,
          randomizarPerguntas
        );
        validationErrorsRef.current = nextValidationErrors;
        setValidationErrors(nextValidationErrors);
      }

      if (updateOriginal) {
        originalRef.current = serialized;
        hasChangesRef.current = false;
        validationErrorsRef.current = {};
        setValidationErrors({});
        setHasChanges(false);
        return;
      }

      if (!hasChangesRef.current) {
        setHasChanges(true);
      }
      hasChangesRef.current = true;
    };

    const getFormulario = async () => {
      if (!hasExistingFormulario) {
        updateFormulario(createEmptyFormulario(), true);
        return;
      }

      setIsLoading(true);
      try {
        const response = await http.get('/FormularioGlobal/Edit', {
          params: {
            id: formularioId,
          },
        });

        updateFormulario(response.data?.model || response.data, true);
      } catch (err) {
        errorHandler(err);
        updateFormulario(createEmptyFormulario(), true, false);
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      if (active && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        getFormulario();
      }
    }, [active]);

    useEffect(() => {
      if (hasLoadedRef.current) {
        getFormulario();
      }
    }, [formularioId]);

    const validateFormularioGlobal = () => {
      const nextValidationErrors = getFormularioValidationErrors(
        formularioRef.current,
        randomizarPerguntas
      );

      validationErrorsRef.current = nextValidationErrors;
      setValidationErrors(nextValidationErrors);

      const toastError =
        getFormularioValidationToastError(nextValidationErrors);
      if (toastError) {
        enqueueSnackbar(
          intl.formatMessage({ id: toastError.id }, toastError.values),
          { variant: 'error' }
        );
      }

      return !hasValidationErrors(nextValidationErrors);
    };

    useImperativeHandle(ref, () => ({
      getFormularioGlobal: () => ({
        ...formularioRef.current,
        randomizarPerguntas,
      }),
      getPerguntasCount: () => countPerguntas(formularioRef.current),
      validateFormularioGlobal,
      saveFormularioGlobal: async (entidadeIdOverride) => {
        const targetEntidadeId = entidadeIdOverride || entidadeId;
        const currentFormulario = formularioRef.current;

        if (
          !targetEntidadeId ||
          targetEntidadeId <= 0 ||
          !hasChangesRef.current
        ) {
          return null;
        }

        if (!validateFormularioGlobal()) {
          return null;
        }

        const payload = serializeFormulario(
          { ...currentFormulario, randomizarPerguntas },
          targetEntidadeId,
          intl
        );

        const response = await http.post('/FormularioGlobal/Save', payload);

        const responseModel = response.data?.model || response.data || {};

        const savedFormulario = normalizeFormulario(
          responseModel.secoes || responseModel.perguntas
            ? responseModel
            : {
                ...payload,
                ...responseModel,
              },
          intl
        );
        updateFormulario(savedFormulario, true);

        return savedFormulario;
      },
    }));

    const isFormularioDisabled =
      !isEntidadePersisted || disabled || formulario.bloqueado;

    const handleColarFormulario = (formularioColado) => {
      updateFormulario({
        ...createEmptyFormulario(),
        ...formularioColado,
        ...copyFormularioStructure(formularioColado),
        id: formularioRef.current?.id || 0,
      });
    };

    return (
      <FormularioGlobalBuilder
        blockedMessage={
          !isEntidadePersisted
            ? intl.formatMessage(
                { id: 'labelSalvePesquisaClimaAntesEditarFormulario' },
                { pesquisa: resources.pesquisa }
              )
            : formulario.bloqueado
            ? intl.formatMessage({
                id: 'labelFormularioGlobalBloqueado',
              })
            : null
        }
        disabled={isFormularioDisabled}
        exibirTipoFormularioIdentificacao={false}
        headerActions={
          <>
            {onPreview && (
              <Button
                color="link"
                leftIcon={Eye}
                iconSize={18}
                onClick={onPreview}
                style={{ color: '#6b7280', padding: 0, textDecoration: 'none' }}
              >
                {intl.formatMessage({ id: 'visualizar' })}
              </Button>
            )}
            <FormularioGlobalCopiarColar
              disabled={isFormularioDisabled || isLoading}
              formulario={formulario}
              onColar={handleColarFormulario}
            />
          </>
        }
        isLoading={isLoading}
        onChange={updateFormulario}
        randomizarPerguntas={randomizarPerguntas}
        tipoFormulario={TIPO_FORMULARIO_PESQUISA_CLIMA}
        validationErrors={validationErrors}
        value={formulario}
      />
    );
  }
);

export default FormularioPesquisaClimaBuilder;
